import { Button } from "@sellar-io/components";
import { CourierDeliveryScheduleListItemResponseType } from "@sellar-io/api-v2/types";
import { daysOfWeek } from "@sellar-io/common";
import dayjs from "dayjs";
import { useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useCourierDeliveryScheduleDelete } from "../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleDelete";
import { useCourierDeliveryScheduleUpdate } from "../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleUpdate";
import { CourierScheduleForm } from "../CourierDeliverySchedule/forms/CourierScheduleForm";
import { CourierDeliveryScheduleFormType } from "./CourierScheduleCreateDialog";

export const CourierScheduleUpdateDialog = ({
  active,
  onDismiss,
  courierDeliverySchedule,
}: {
  active: boolean;
  onDismiss: () => void;
  courierDeliverySchedule: CourierDeliveryScheduleListItemResponseType;
}) => {
  const scrollToRef = useRef<HTMLDivElement | null>(null);
  const formId = useId();
  const form = useForm<CourierDeliveryScheduleFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      orderCutOffTime: `${dayjs(courierDeliverySchedule.orderCutOffTime)
        .utc()
        .get("hour")}:${
        dayjs(courierDeliverySchedule.orderCutOffTime).utc().get("minutes") ===
        0
          ? "00"
          : dayjs(courierDeliverySchedule.orderCutOffTime)
                .utc()
                .get("minutes") < 10
            ? `0${dayjs(courierDeliverySchedule.orderCutOffTime)
                .utc()
                .get("minutes")}`
            : dayjs(courierDeliverySchedule.orderCutOffTime)
                .utc()
                .get("minutes")
      }`,
      dispatchDaysAfterConfirmation:
        courierDeliverySchedule.dispatchDaysAfterConfirmation,
      deliveryDaysAfterDispatch:
        courierDeliverySchedule.deliveryDaysAfterDispatch,
      startAt: dayjs(courierDeliverySchedule.startAt).format("YYYY-MM-DD"),
      allowSaturdayDelivery: courierDeliverySchedule.allowSaturdayDelivery,
    },
  });

  const existingDays = courierDeliverySchedule.CourierDeliveryScheduleDay.map(
    (courierDeliveryDay) => courierDeliveryDay.day,
  );
  const [selectedDays, setSelectedDays] = useState<daysOfWeek[]>(existingDays);

  const [showWarning, setShowWarning] = useState<boolean>(false);

  const courierDeliveryScheduleUpdateMutation =
    useCourierDeliveryScheduleUpdate(courierDeliverySchedule.id);
  const courierDeliveryScheduleDeleteMutation =
    useCourierDeliveryScheduleDelete(courierDeliverySchedule.id);

  const onSubmit = (data: CourierDeliveryScheduleFormType) => {
    if (!selectedDays.length) {
      setShowWarning(true);
      return scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    courierDeliveryScheduleUpdateMutation.mutate(
      {
        orderCutOffTime: dayjs()
          .utc()
          .set("hour", parseInt(data.orderCutOffTime.split(":")[0] ?? ""))
          .set("minute", parseInt(data.orderCutOffTime.split(":")[1] ?? ""))
          .toDate(),
        dispatchDaysAfterConfirmation: parseInt(
          data.dispatchDaysAfterConfirmation.toString(),
        ),
        deliveryDaysAfterDispatch: parseInt(
          data.deliveryDaysAfterDispatch.toString(),
        ),
        days: selectedDays,
        startAt: data.startAt ? new Date(data.startAt) : null,
        allowSaturdayDelivery: data.allowSaturdayDelivery,
      },
      {
        onSuccess: () => onDismiss(),
      },
    );
  };

  const onDelete = async () => {
    await courierDeliveryScheduleDeleteMutation.mutateAsync();
    onDismiss();
  };

  const description =
    "Give your customers an accurate delivery date prediction for orders fulfilled by courier. Your given dispatch and delivery dates will be automatically pre-filled when finalising courier orders.";

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <div ref={scrollToRef}></div>
      <DialogNavigation
        title={"Update courier delivery schedule"}
        description={description}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {courierDeliveryScheduleUpdateMutation.isError ||
        courierDeliveryScheduleDeleteMutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(courierDeliveryScheduleUpdateMutation.error)
                ?.response?.data.message ??
              apiV2AssertError(courierDeliveryScheduleDeleteMutation.error)
                ?.response?.data.message ??
              "Unknown error occurred."
            }
          />
        ) : null}
        <CourierScheduleForm
          setSelectedDays={setSelectedDays}
          selectedDays={selectedDays}
          form={form}
          onSubmit={onSubmit}
          showWarning={showWarning}
          formId={formId}
        />
      </DialogContent>
      <DialogCTA>
        <Button
          color={"red"}
          loading={courierDeliveryScheduleDeleteMutation.isPending}
          onClick={onDelete}
        >
          delete
        </Button>
        <Button
          type="submit"
          form={formId}
          loading={courierDeliveryScheduleUpdateMutation.isPending}
        >
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
